import React, { useEffect, useRef, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  Select,
  Switch,
  Button,
  Form as AntForm,
  Input as AntInput,
  Space as Stack,
  Spin,
  Descriptions,
  Tag,
} from 'antd';
import {
  get_product_list_both,
  get_product_by_id,
  bp_inventory_data,
  get_recipe_meta_form,
  edit_recipe_meta_data,
  get_recipe_by_product_id,
  get_output_form_type_list,
} from '../../../utils/api';
import { get_units } from '../mbmr/mbmr_api';
import useAppSetting, { useMbmrAlias } from '@/hooks/useAppSetting';
import { toast } from 'react-toastify';
import { ackApi } from '@/utils/ackApi';
import { useSelector, useDispatch } from 'react-redux';
import { addField } from '@/store/reducers/headerContent';
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import RecipeBomVariantsTabs from './RecipeBomVariantsTabs';

// ==============================|| EDIT RECIPE MODAL ||============================== //

// mfrContext: 2026-09-01, "get from the mfr part ... select product and variant selection part
// exists ... align this" — when opened from the MFR flow ("Edit BOM & variants"), the product /
// dosage form / variant are already fixed by the MFR's own header (Dosage form -> Variant
// cascade), not editable here — pass { productLabel, dosageFormLabel, variantLabel } to show them
// as a read-only banner instead of leaving this generic Output/Product section looking unrelated
// to where the popup was opened from. Purely additive — omitted (undefined) for every other
// caller, so this doesn't change EditRecipeModal's normal (non-MFR) behavior at all.

/** Minimum / maximum batch size, no longer asked: they bracket the master batch size. */
export const minMaxFromSize = (size, min, max) => {
  const n = Number(size);
  if (size === '' || size == null || !Number.isFinite(n) || n <= 0) {
    return { min_quantity: min === '' || min == null ? 0 : min, max_quantity: max === '' || max == null ? 0 : max };
  }
  return { min_quantity: Math.floor(n), max_quantity: Math.ceil(n) };
};

function EditRecipeModal({ open, onClose, recipeId, onSuccess, viewOnly = false, mfrContext, hideAddVariant }) {
  const { isDark } = useTheme();
  const dispatch = useDispatch();
  const selectedModule = useSelector((state) => state.moduleView);
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const labelColor = isDark ? '#9ca3af' : '#6b7280';
  const borderColor = isDark ? '#374151' : '#e5e7eb';
  const headerBg = isDark ? defaultColors.palette.gray[700] : '#f8fafc';

  // Project theme colors (Industrial Teal palette)
  const primaryColor = isDark ? defaultColors.palette.teal[500] : defaultColors.palette.teal[600];
  const primaryLightBg = isDark ? defaultColors.palette.teal[900] : defaultColors.palette.teal[50];
  const primaryBorder = isDark ? defaultColors.palette.teal[700] : defaultColors.palette.teal[200];
  const primaryLabelText = isDark ? defaultColors.palette.teal[300] : defaultColors.palette.teal[700];

  // Whether Input Based Batch Size is available at all (App Settings) — decides
  // whether the per-recipe Batch Size Mode toggle is offered. Output Based is
  // always available (locked on there).
  const canUseInputMode = useAppSetting('Input Based Batch Size');
  const mbmrAlias = useMbmrAlias();

  const [loading, setLoading] = useState(false);
  const [product_list, set_product_list] = useState([]);
  const [unit_list, set_unit_list] = useState([]);
  const [inventory_list, set_inventory_list] = useState([]);
  // Recipes for this recipe's (fixed) product — filtered to output_type ===
  // "Intermediate" for the "Select Recipe" (input-based upstream) picker.
  const [product_recipe_list, set_product_recipe_list] = useState([]);
  const [output_form_type_list, set_output_form_type_list] = useState([]);

  // Pre-filled values fetched from the API
  const [initialValues, setInitialValues] = useState(null);

  // Validation — same rules as RecipeMetaData. master_batch_size/unit/
  // source_mbmr_id/input_based_ingredient_quantity key off the live
  // `batch_size_mode` value, not the raw app setting.
  const validationSchema = Yup.object().shape({
    recipe_name: Yup.string().required('Name is required'),
    description: Yup.string().required('Description is required'),
    master_batch_size: Yup.number().when('batch_size_mode', {
      is: 'Input',
      then: (s) => s.nullable(),
      otherwise: (s) =>
        s
          .min(1, 'Must be greater than 0')
          .required('Master batch size is required'),
    }),
    // Minimum / Maximum are not asked (hidden on every record, not only an MFR's content_recipe);
    // on save they bracket the master batch size (floor / ceil), see the payload below.
    unit: Yup.mixed().when('batch_size_mode', {
      is: 'Input',
      then: (s) => s.nullable(),
      otherwise: (s) => s.required('Unit is required'),
    }),
    source_mbmr_id: Yup.string().nullable().when('batch_size_mode', {
      is: 'Input',
      then: (s) => s.required('Select an upstream recipe'),
    }),
    input_based_ingredient_quantity: Yup.number().when('batch_size_mode', {
      is: 'Input',
      then: (s) => s.min(0.0001, 'Must be greater than 0').required('Quantity is required'),
      otherwise: (s) => s.nullable(),
    }),
    output_material_id: Yup.string().nullable().when('output_type', {
      is: 'Intermediate',
      then: (s) => s.required('Select the output intermediate'),
    }),
  });

  // Load dropdown data + pre-fill form whenever the modal opens
  useEffect(() => {
    if (!open || !recipeId) return;

    setLoading(true);
    setInitialValues(null);

    // Fetch dropdowns + recipe data in parallel.
    // recipe_type=false so the product is listed even if it has no production recipe.
    get_product_list_both({ module_type: selectedModule, recipe_type: false }, (res) =>
      set_product_list(res?.data?.data || [])
    );
    get_units((res) => {
      if (res?.data?.status === 'success') set_unit_list(res?.data?.data || []);
    });
    bp_inventory_data({}, (res) => {
      if (res?.data?.status === 'success') set_inventory_list(res?.data?.data || []);
    });
    get_output_form_type_list((res) => {
      if (res?.data?.status === 'success') set_output_form_type_list(res?.data?.data || []);
    });

    // Fetch recipe metadata to pre-fill
    get_recipe_meta_form((res) => {
      setLoading(false);
      if (res?.data?.status === 'success') {
        const d = res.data.data;
        setInitialValues({
          product_id:                      d.product_id ?? '',
          recipe_name:                     d.recipe_name ?? '',
          description:                     d.description ?? '',
          change_control_id:               d.change_control_id ?? '',
          master_batch_size:               d.master_batch_size ?? '',
          unit:                            d.unit ?? '',           // unit ID from API
          // 2026-09-01: "set min and max same as this size" — for an MFR's content_recipe these
          // are hidden (no real batch-size range applies) but still back master_batch_size's own
          // "within min/max" validation; start them equal to it (not the backend's raw 0/0
          // defaults) so that check can't fail on a value the user never even sees.
          // floor/ceil, not the same fractional value twice — min_quantity/max_quantity are
          // IntegerFields, so an exact-but-fractional match would truncate on save and drift out
          // of range again next load (see the onChange handler below for the same reasoning).
          min_quantity:                    mfrContext && d.master_batch_size != null ? Math.floor(d.master_batch_size) : (d.min_quantity ?? ''),
          max_quantity:                    mfrContext && d.master_batch_size != null ? Math.ceil(d.master_batch_size) : (d.max_quantity ?? ''),
          actual_quantity:                 d.actual_quantity ?? '',
          is_continuous:                   d.is_continuous ?? false,
          dispense_mass_uom:               d.dispense_mass_uom || 'kg',
          // Fixed fact about this recipe — this modal has no "Is Packaging
          // MBMR" toggle, so packaging status is never changed here; used
          // only to decide whether to show the Output selector at all.
          is_packaging:                    d.is_packaging ?? false,
          // Per-recipe Batch Size Mode, derived from the stored flag.
          batch_size_mode:                 d.is_input_based_batch_size ? 'Input' : 'Output',
          source_mbmr_id:                  d.source_mbmr_id ?? null,
          input_based_batch_ingredient_id: d.input_based_batch_ingredient_id ?? null,
          input_based_batch_ingredient:    d.input_based_batch_ingredient ?? null,
          input_based_ingredient_quantity: d.input_based_ingredient_quantity ?? '',
          input_based_batch_unit:          d.unit ?? null,
          // What this recipe itself produces.
          output_type:                     d.output_type ?? 'Product',
          output_material_id:              d.output_material_id ?? null,
          output_material_name:            d.output_material_name ?? null,
          output_form_id:                  d.output_form_id ?? null,
        });
        // Candidates for "Select Recipe" — this recipe's own product is fixed,
        // so fetch once against it rather than re-fetching on every change.
        // include_unfinalized: this modal has no packaging picker, so the
        // fetch exists solely to feed Select Recipe — which needs any
        // test-phase Intermediate-output recipe, not just finalized ones.
        if (d.product_id) {
          get_product_by_id(d.product_id, (pres) => {
            if (pres?.data?.status === 'success' && pres?.data?.data) {
              const p = pres.data.data;
              const id = Number(p.id ?? p.product_id);
              const row = { product_id: id, product_name: p.product_name || p.name || '' };
              set_product_list((prev) => {
                const list = prev || [];
                if (list.some((x) => Number(x.product_id) === id)) return list;
                return [row, ...list];
              });
            }
          });
          get_recipe_by_product_id((r) => {
            const rows = r?.data?.status === 'success' ? (r?.data?.data || []) : [];
            set_product_recipe_list(rows);
          }, { product_id: d.product_id, include_unfinalized: true });
        }
      } else {
        toast.error('Failed to load MBMR data');
        onClose();
      }
    }, { recipe_id: recipeId });
  }, [open, recipeId]);

  // 2026-10-21 (owner) · the MBMR's general instructions are back — OPTIONAL. Unsaved edits in the General
  // instructions tab are saved by Update too; on the tablet they are merged with each process's instruction.
  const instructionsRef = useRef(null);   // the General instructions tab's latest state (unsaved text included)
  const handleSubmit = async (values, helpers) => {
    const st = instructionsRef.current;
    if (!hideAddVariant && st?.loaded && st.dirty) {
      const r = await ackApi.saveRecipeInstructions(recipeId, {
        general_instructions: st.text, abbreviations_json: st.abbreviations, change_reason: 'MBMR general instructions edited',
      });
      if (r?.status !== 'success') {
        toast.error(`General instructions not saved: ${r?.message || 'error'}`);
        helpers?.setSubmitting?.(false);
        return;
      }
    }
    return submitRecipe(values, helpers);
  };

  const submitRecipe = (values, { setSubmitting }) => {
    const isRecipeInputMode = values.batch_size_mode === 'Input';
    const payload = {
      recipe_id: recipeId,
      recipe_name: values.recipe_name,
      description: values.description,
      // Boolean flag marking the recipe as input-based. Backend reads this exact key.
      is_input_based_ingredient_quantity: isRecipeInputMode,
      change_control_id: values.change_control_id,
      master_batch_size: values.master_batch_size,
      unit: values.unit,
      // Not asked on the form: the range is the master batch size itself (integer columns, so
      // floor / ceil keep a fractional size inside it).
      ...minMaxFromSize(values.master_batch_size, values.min_quantity, values.max_quantity),
      actual_quantity:
        values.actual_quantity === '' || values.actual_quantity == null
          ? (isRecipeInputMode ? 1 : values.master_batch_size || 0)
          : values.actual_quantity,
      is_continuous: values.is_continuous,
      dispense_mass_uom: values.dispense_mass_uom || 'kg',
      // Output declaration — what this recipe itself produces.
      output_type: values.output_type || 'Product',
      output_material_id: values.output_type === 'Intermediate' ? (values.output_material_id || null) : null,
      output_form_id: values.output_form_id || null,
      // This modal has no "Is Packaging MBMR" toggle, so never touch
      // source_mbmr_id for a packaging recipe — leave whatever it already has.
      ...(!initialValues?.is_packaging && {
        source_mbmr_id: isRecipeInputMode ? (values.source_mbmr_id || null) : null,
      }),
      ...(isRecipeInputMode && {
        input_based_batch_ingredient_id: values.input_based_batch_ingredient_id,
        input_based_batch_ingredient: values.input_based_batch_ingredient,
        input_based_ingredient_quantity: values.input_based_ingredient_quantity,
        input_based_batch_unit: values.input_based_batch_unit,
      }),
    };

    edit_recipe_meta_data(payload, (res) => {
      setSubmitting(false);
      if (res?.status === 'failed' || res?.data?.status === 'error') {
        toast.error(res?.message || res?.data?.message || 'Failed to update MBMR');
      } else {
        toast.success(res?.message || res?.data?.message || 'MBMR updated successfully');
        // Update sidebar recipe name immediately without a page refresh
        dispatch(addField({ key: 'MBMR', value: payload.recipe_name }));
        onSuccess?.();
        onClose();
      }
    });
  };

  const handleClose = () => {
    setInitialValues(null);
    onClose();
  };

  // Batch Size unit options — restricted to the selected Output Form's
  // allowed unit categories (WEIGHT/VOLUME/COUNT). No Output Form selected,
  // or one with an empty (unrestricted) category list, offers every unit.
  const getBatchUnitOptions = (outputFormId) => {
    const form = (output_form_type_list || []).find((f) => f.output_form_type_id === outputFormId);
    const categories = form?.unit_categories || [];
    const list = categories.length
      ? (unit_list || []).filter((u) => categories.includes(u.unit_category))
      : (unit_list || []);
    return list.map((u) => ({ value: u.id, label: u.unit_name }));
  };

  return (
    <Modal
      title={<span style={{ color: textColor }}>{viewOnly ? 'View MBMR' : 'Edit MBMR'}</span>}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      centered
      width={1200}
      styles={{
        content: { backgroundColor: cardBgColor },
        header: { backgroundColor: cardBgColor },
        // 2026-10-12 · the dialog never grows past the screen: its body scrolls inside itself,
        // so a long BOM or instructions list does not scroll the page behind it.
        body: { maxHeight: 'calc(100vh - 140px)', overflowY: 'auto', overflowX: 'hidden', paddingRight: 4 },
      }}
    >
      {loading || !initialValues ? (
        <div className="flex justify-center items-center py-10">
          <Spin size="large" />
        </div>
      ) : viewOnly ? (
        (() => {
          const productName = product_list.find(p => Number(p.product_id) === Number(initialValues.product_id))?.product_name || '-';
          const unitName = unit_list.find(u => Number(u.id) === Number(initialValues.unit))?.unit_name || '';
          const ingredientName = inventory_list.find(i => i.material_id === initialValues.input_based_batch_ingredient_id)?.material_name || '-';
          // What THIS recipe is actually set to, not the app-wide setting.
          const isRecipeInputMode = initialValues.batch_size_mode === 'Input';
          const upstreamRecipeName = product_recipe_list.find(r => r.recipe_id === initialValues.source_mbmr_id)?.recipe_name || '-';

          const descLabelStyle = {
            color: primaryLabelText,
            fontWeight: 600,
            backgroundColor: primaryLightBg,
            width: 160,
            fontSize: 13,
          };
          const descContentStyle = {
            color: textColor,
            backgroundColor: cardBgColor,
            fontWeight: 500,
          };

          // Build only items that have a real value
          const detailItems = [
            initialValues.product_id && productName
              ? { label: 'Product', value: productName, span: 2 }
              : null,
            initialValues.change_control_id
              ? { label: 'Change Control', value: initialValues.change_control_id, span: 2 }
              : null,
            initialValues.output_type === 'Intermediate'
              ? { label: 'Output', value: initialValues.output_material_name || '-', span: 2 }
              : null,
            output_form_type_list.find((o) => o.output_form_type_id === initialValues.output_form_id)
              ? { label: 'Output Form', value: output_form_type_list.find((o) => o.output_form_type_id === initialValues.output_form_id)?.name, span: 2 }
              : null,
            isRecipeInputMode
              ? { label: 'Upstream Recipe', value: upstreamRecipeName, span: 2 }
              : null,
            isRecipeInputMode
              ? (initialValues.input_based_batch_ingredient_id && ingredientName
                  ? { label: 'Batch RM', value: ingredientName, span: 2 }
                  : null)
              : (initialValues.master_batch_size
                  ? { label: 'Master Batch Size', value: `${initialValues.master_batch_size}${unitName ? ` ${unitName}` : ''}` }
                  : null),
            isRecipeInputMode && initialValues.input_based_ingredient_quantity
              ? { label: 'Quantity', value: `${initialValues.input_based_ingredient_quantity}${unitName ? ` ${unitName}` : ''}` }
              : null,
            // Min / Max Quantity are not shown (no longer asked; they bracket the master size).
            initialValues.actual_quantity
              ? { label: 'Actual Quantity', value: initialValues.actual_quantity }
              : null,
            selectedModule === 'pack'
              ? { label: 'Continuous Running', isBool: true, checked: initialValues.is_continuous }
              : null,
            { label: 'Dispense mass in', value: initialValues.dispense_mass_uom === 'g' ? 'Grams (g)' : 'Kilograms (kg)' },
          ].filter(Boolean);

          return (
            <div>
              {/* Recipe header — solid primary teal banner */}
              <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: primaryColor }}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p
                      className="text-xs mb-1 uppercase font-medium"
                      style={{ color: defaultColors.palette.teal[100], letterSpacing: '0.08em' }}
                    >
                      {mbmrAlias} Name
                    </p>
                    <h2 className="text-lg font-bold" style={{ color: '#ffffff', margin: 0, lineHeight: 1.3 }}>
                      {initialValues.recipe_name || '-'}
                    </h2>
                    {initialValues.description && (
                      <p className="text-sm mt-1" style={{ color: defaultColors.palette.teal[100], margin: 0 }}>
                        {initialValues.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Details grid — only rows with actual values */}
              {detailItems.length > 0 && (
                <Descriptions
                  bordered
                  size="small"
                  column={2}
                  labelStyle={descLabelStyle}
                  contentStyle={descContentStyle}
                >
                  {detailItems.map((item) => (
                    <Descriptions.Item key={item.label} label={item.label} span={item.span || 1}>
                      {item.isBool ? (
                        <Tag color={item.checked ? 'success' : 'default'}>
                          {item.checked ? 'Yes' : 'No'}
                        </Tag>
                      ) : (
                        item.value
                      )}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              )}

              <div className="flex justify-end mt-4 pt-3" style={{ borderTop: `1px solid ${primaryBorder}` }}>
                <Button
                  type="primary"
                  onClick={handleClose}
                  style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                >
                  Close
                </Button>
              </div>
            </div>
          );
        })()
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, handleChange, handleSubmit, setFieldValue, errors, touched, isSubmitting }) => (
            <AntForm layout="vertical" onFinish={handleSubmit} className="mt-2">
              {mfrContext && (
                <div className="rounded-lg mb-4 px-3 py-2" style={{ background: isDark ? defaultColors.palette.gray[800] : '#eff6ff', border: `1px solid ${isDark ? defaultColors.palette.gray[700] : '#bfdbfe'}` }}>
                  <Stack size={16} wrap>
                    {mfrContext.productLabel && <span><span className="text-xs opacity-70">Product: </span><Tag color="default">{mfrContext.productLabel}</Tag></span>}
                    {mfrContext.dosageFormLabel && <span><span className="text-xs opacity-70">Dosage form: </span><Tag color="blue">{mfrContext.dosageFormLabel}</Tag></span>}
                    {mfrContext.variantLabel && <span><span className="text-xs opacity-70">Variant: </span><Tag color="geekblue">{mfrContext.variantLabel}</Tag></span>}
                  </Stack>
                  <div className="text-xs opacity-60 mt-1">Fixed from this MFR's own product / dosage form / variant selection — not editable here.</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-x-4">
                {/* Recipe Name */}
                <AntForm.Item
                  label="Name"
                  validateStatus={touched.recipe_name && errors.recipe_name ? 'error' : ''}
                  help={touched.recipe_name && errors.recipe_name}
                >
                  <AntInput
                    name="recipe_name"
                    size="large"
                    value={values.recipe_name}
                    onChange={handleChange}
                    placeholder="Enter Name"
                  />
                </AntForm.Item>

                {/* Description */}
                <AntForm.Item
                  label="Description"
                  validateStatus={touched.description && errors.description ? 'error' : ''}
                  help={touched.description && errors.description}
                >
                  <AntInput
                    name="description"
                    size="large"
                    value={values.description}
                    onChange={handleChange}
                    placeholder="Enter Description"
                  />
                </AntForm.Item>

                {/* Change Control */}
                <AntForm.Item
                  label="Change Control"
                  validateStatus={touched.change_control_id && errors.change_control_id ? 'error' : ''}
                  help={touched.change_control_id && errors.change_control_id}
                >
                  <AntInput
                    name="change_control_id"
                    size="large"
                    value={values.change_control_id}
                    onChange={handleChange}
                    placeholder="Enter Change Control"
                  />
                </AntForm.Item>

                {/* Output Form — dosage/physical form this recipe produces. 2026-09-01: "that
                    output form cannot be editable independently — that is set based on variant
                    selected and dosage form" — for an MFR's content_recipe it's already fixed
                    (set from the dosage form at creation — see mfr_collection), same as Product. */}
                <AntForm.Item label="Output Form">
                  <Select
                    showSearch
                    allowClear
                    size="large"
                    disabled={!!mfrContext}
                    placeholder="Select output form (optional)"
                    optionFilterProp="label"
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    value={values.output_form_id || undefined}
                    onChange={(val) => {
                      setFieldValue('output_form_id', val || null);
                      // Selected batch-size unit may no longer be in the newly
                      // chosen Output Form's allowed categories — clear it
                      // rather than silently leaving an invalid combination.
                      const stillValid = getBatchUnitOptions(val || null).some(
                        (o) => o.value === values.unit
                      );
                      if (values.unit && !stillValid) {
                        setFieldValue('unit', '');
                      }
                    }}
                    options={(output_form_type_list || []).map((item) => ({
                      value: item.output_form_type_id,
                      label: item.name,
                    }))}
                  />
                </AntForm.Item>

                {/* Output — what THIS recipe itself produces. Hidden for a
                    packaging MBMR, whose output is always the Product (this
                    modal has no toggle to change is_packaging). */}
                {!values.is_packaging && (
                  <AntForm.Item label="Output">
                    <Select
                      size="large"
                      value={values.output_type}
                      onChange={(val) => {
                        setFieldValue('output_type', val);
                        if (val !== 'Intermediate') {
                          setFieldValue('output_material_id', null);
                          setFieldValue('output_material_name', null);
                        }
                      }}
                      options={[
                        { value: 'Product', label: 'Product' },
                        { value: 'Intermediate', label: 'Intermediate' },
                      ]}
                    />
                  </AntForm.Item>
                )}

                {/* Product (always read-only) / Output Intermediate — same
                    slot, mutually exclusive. */}
                {values.is_packaging || values.output_type !== 'Intermediate' ? (
                  <AntForm.Item label="Product">
                    <Select
                      showSearch
                      disabled
                      size="large"
                      optionFilterProp="label"
                      value={values.product_id ? Number(values.product_id) : undefined}
                      options={(product_list || []).map((item) => ({
                        value: Number(item?.product_id ?? item?.id),
                        label: item?.product_name || item?.name || '',
                      }))}
                      placeholder="Product"
                    />
                  </AntForm.Item>
                ) : (
                  <AntForm.Item
                    label="Output Intermediate"
                    validateStatus={touched.output_material_id && errors.output_material_id ? 'error' : ''}
                    help={touched.output_material_id && errors.output_material_id}
                  >
                    <Select
                      showSearch
                      allowClear
                      size="large"
                      placeholder="Select Intermediate"
                      value={values.output_material_id || undefined}
                      onChange={(val, option) => {
                        setFieldValue('output_material_id', val || null);
                        setFieldValue('output_material_name', option?.label || null);
                      }}
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={inventory_list?.filter((i) => i.material_type === 'By Product')?.map((i) => ({
                        value: i.material_id,
                        label: i.material_name,
                      }))}
                    />
                  </AntForm.Item>
                )}

                {/* Batch Size Mode — only offered as a real per-recipe choice
                    when Input Based Batch Size is enabled in App Settings and
                    this isn't a packaging MBMR. */}
                {canUseInputMode && !values.is_packaging && (
                  <AntForm.Item label="Batch Size Mode">
                    <Switch
                      checked={values.batch_size_mode === 'Input'}
                      onChange={(checked) => {
                        const mode = checked ? 'Input' : 'Output';
                        setFieldValue('batch_size_mode', mode);
                        if (mode === 'Output') {
                          setFieldValue('source_mbmr_id', null);
                          setFieldValue('input_based_batch_ingredient_id', null);
                          setFieldValue('input_based_batch_ingredient', null);
                          setFieldValue('input_based_ingredient_quantity', '');
                        }
                      }}
                      checkedChildren="Input"
                      unCheckedChildren="Output"
                    />
                  </AntForm.Item>
                )}

                {/* Master Batch Size + Unit OR Select Recipe (Input mode) + Quantity */}
                {values.batch_size_mode === 'Input' ? (
                  <>
                    <AntForm.Item
                      label="Select Recipe"
                      validateStatus={touched.source_mbmr_id && errors.source_mbmr_id ? 'error' : ''}
                      help={touched.source_mbmr_id && errors.source_mbmr_id}
                    >
                      <Select
                        showSearch
                        allowClear
                        size="large"
                        placeholder="Select upstream recipe"
                        optionFilterProp="label"
                        value={values.source_mbmr_id || undefined}
                        notFoundContent="No Intermediate-output recipe for this product"
                        options={(product_recipe_list || [])
                          .filter((r) => r.output_type === 'Intermediate')
                          .map((r) => ({ value: r.recipe_id, label: r.recipe_name }))}
                        onChange={(val) => {
                          setFieldValue('source_mbmr_id', val || null);
                          const src = (product_recipe_list || []).find((r) => r.recipe_id === val);
                          setFieldValue('input_based_batch_ingredient_id', src?.output_material_id || null);
                          setFieldValue('input_based_batch_ingredient', src?.output_material_name || null);
                          const mat = inventory_list?.find((i) => i.material_id === src?.output_material_id);
                          setFieldValue('input_based_batch_unit', mat?.unit_id || null);
                          setFieldValue('unit', mat?.unit_id || null);
                          setFieldValue('input_based_ingredient_quantity', '');
                        }}
                      />
                    </AntForm.Item>

                    {values.source_mbmr_id && (
                      <AntForm.Item label="Batch RM">
                        <Tag color="green">{values.input_based_batch_ingredient || 'Unknown material'}</Tag>
                      </AntForm.Item>
                    )}

                    {values.source_mbmr_id && (
                      <AntForm.Item
                        label="Quantity"
                        validateStatus={
                          touched.input_based_ingredient_quantity && errors.input_based_ingredient_quantity
                            ? 'error'
                            : ''
                        }
                        help={touched.input_based_ingredient_quantity && errors.input_based_ingredient_quantity}
                      >
                        <Stack.Compact style={{ width: '100%' }}>
                          <AntInput
                            type="number"
                            name="input_based_ingredient_quantity"
                            size="large"
                            placeholder="Master size this batch is prepared against"
                            value={values.input_based_ingredient_quantity}
                            onChange={handleChange}
                            style={{ width: '60%' }}
                          />
                          <Select
                            showSearch
                            allowClear
                            size="large"
                            placeholder="Unit"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                              String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            value={values.unit || undefined}
                            onChange={(val) => setFieldValue('unit', val || '')}
                            // Auto-filled from the selected recipe's output material — locked
                            // so it stays in sync with that material's own unit.
                            disabled={!!values.input_based_batch_ingredient_id}
                            title={
                              values.input_based_batch_ingredient_id
                                ? 'Auto-filled from the selected recipe’s output material'
                                : ''
                            }
                            style={{ width: '40%' }}
                            options={getBatchUnitOptions(values.output_form_id)}
                          />
                        </Stack.Compact>
                      </AntForm.Item>
                    )}
                  </>
                ) : mfrContext ? (
                  // 2026-09-01, corrected same day: "that is the reason we need to set 1 tablet
                  // = what is the value and units" — content_recipe.unit is a real WEIGHT unit
                  // now (mg — see mfr_view.mfr_collection, "the material units are not tablets"),
                  // not the COUNT unit ("tablets") it used to be, which is what caused the "116"
                  // bug in the first place (a COUNT unit can never appear in this WEIGHT-only
                  // picker). So this IS now editable again, on purpose: it's the real per-tablet/
                  // per-capsule weight (e.g. 78.258 mg), known from the actual spec, not something
                  // to leave at a meaningless placeholder — everything downstream (every BOM
                  // line's Std Qty) is expressed in this same unit. Only Output Form/Product above
                  // stay locked — those really are fixed by the MFR header, this genuinely isn't.
                  <AntForm.Item
                    label="Formula Basis"
                    validateStatus={
                      (touched.master_batch_size && errors.master_batch_size) ||
                      (touched.unit && errors.unit) ? 'error' : ''
                    }
                    help={
                      (touched.master_batch_size && errors.master_batch_size) ||
                      (touched.unit && errors.unit) ||
                      `e.g. the real weight of one ${mfrContext.countUnitLabel || 'unit'}, once known — every ingredient quantity below is expressed against this`
                    }
                  >
                    <Stack.Compact style={{ width: '100%' }}>
                      {/* 2026-09-02, "formula basis 1 unit = weight mg ... so user understands
                          better" — spell the equation out on the field itself (1 Tablet = ___ mg)
                          instead of only saying "weight of 1 Tablets" in the label above it. */}
                      <AntInput
                        type="number"
                        name="master_batch_size"
                        size="large"
                        addonBefore={`1 ${mfrContext.countUnitLabel || 'unit'} =`}
                        placeholder="e.g. 78.258"
                        value={values.master_batch_size}
                        onChange={(e) => {
                          handleChange(e);
                          // 2026-09-01: "set min and max same as this size" — Minimum/Maximum are
                          // hidden here (there's no real batch-size range for an MFR's content),
                          // but master_batch_size's own validation still checks it falls within
                          // them, and they default to 0/0 from the backend — kept silently equal
                          // to this value so that check can never fire on a field the user can't
                          // even see or fix.
                          // Real bug, caught by reproducing the exact failing payload against the
                          // backend: handleChange() above parses type="number" inputs to a float
                          // itself (Formik's own built-in behavior) before storing master_batch_size
                          // — e.target.value here is still the raw string, so setFieldValue must
                          // parse it the same way, or min_quantity/max_quantity save as strings
                          // ("78.258") against an IntegerField and the update 500s.
                          // min_quantity/max_quantity are themselves IntegerFields — an equal-but-
                          // fractional value (78.258/78.258) would truncate to 78/78 on save, then
                          // fail "within min/max" again next time (78.258 > 78). floor/ceil instead,
                          // so the (integer) bounds always safely bracket the real fractional weight.
                          const parsed = e.target.value === '' ? '' : parseFloat(e.target.value);
                          setFieldValue('min_quantity', parsed === '' ? parsed : Math.floor(parsed));
                          setFieldValue('max_quantity', parsed === '' ? parsed : Math.ceil(parsed));
                        }}
                        style={{ width: '60%' }}
                      />
                      <Select
                        showSearch
                        allowClear
                        size="large"
                        placeholder="Unit"
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        value={values.unit || undefined}
                        onChange={(val) => setFieldValue('unit', val || '')}
                        style={{ width: '40%' }}
                        options={getBatchUnitOptions(values.output_form_id)}
                      />
                    </Stack.Compact>
                  </AntForm.Item>
                ) : (
                  <AntForm.Item
                    label="Master Batch Size"
                    validateStatus={
                      (touched.master_batch_size && errors.master_batch_size) ||
                      (touched.unit && errors.unit) ? 'error' : ''
                    }
                    help={
                      (touched.master_batch_size && errors.master_batch_size) ||
                      (touched.unit && errors.unit)
                    }
                  >
                    <Stack.Compact style={{ width: '100%' }}>
                      <AntInput
                        type="number"
                        name="master_batch_size"
                        size="large"
                        placeholder="Enter Batch Size"
                        value={values.master_batch_size}
                        onChange={handleChange}
                        style={{ width: '60%' }}
                      />
                      <Select
                        showSearch
                        allowClear
                        size="large"
                        placeholder="Unit"
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        value={values.unit || undefined}
                        onChange={(val) => setFieldValue('unit', val || '')}
                        style={{ width: '40%' }}
                        options={getBatchUnitOptions(values.output_form_id)}
                      />
                    </Stack.Compact>
                  </AntForm.Item>
                )}

              </div>

              {/* Continuous Running — same condition as creation (pack module only) */}
              {selectedModule === 'pack' && (
                <AntForm.Item label="Continuous Running">
                  <Switch
                    checked={values.is_continuous}
                    onChange={(val) => setFieldValue('is_continuous', val)}
                  />
                </AntForm.Item>
              )}

              {/* 2026-10-16 (owner) · the unit stores weigh this MBMR's mass materials in. Fixed onto each
                  batch as it is issued — batches already issued keep their unit. */}
              <AntForm.Item label="Dispense mass in"
                            extra="BOM, ARs, calculation and dispensing targets of new batches use this unit. Batches already issued keep theirs.">
                <Select
                  value={values.dispense_mass_uom || 'kg'}
                  onChange={(val) => setFieldValue('dispense_mass_uom', val)}
                  style={{ width: 220 }}
                  disabled={viewOnly}
                  options={[{ value: 'kg', label: 'Kilograms (kg)' }, { value: 'g', label: 'Grams (g)' }]}
                />
              </AntForm.Item>

              <div className="pt-3 border-t border-gray-200">
                {/* 2026-10-15 (owner) · the BOM tab follows the batch size typed above, live */}
                <RecipeBomVariantsTabs recipeId={recipeId} hideAddVariant={hideAddVariant} showVariants={!!hideAddVariant}
                                       batchSize={values.master_batch_size}
                                       batchUom={(unit_list || []).find((u) => Number(u.id) === Number(values.unit))?.unit_name || ''}
                                       massUom={values.dispense_mass_uom || 'kg'}
                                       showInstructions={!hideAddVariant} readOnly={viewOnly}
                                       onInstructionsChange={(st) => { instructionsRef.current = st; }} />
              </div>

              {/* Footer buttons */}
              <div className="flex justify-end gap-3 mt-4 pt-3 border-t border-gray-200">
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                >
                  Update
                </Button>
              </div>
            </AntForm>
          )}
        </Formik>
      )}
    </Modal>
  );
}

export default EditRecipeModal;
